import {
  CreateComponent,
  CreateDevice,
  CreateSubComponent,
  DocumentTemplate,
  Suggestion as TSuggestion,
} from "@models";
import type {} from "@mui/lab/themeAugmentation";

import {
  BaseUser,
  Component,
  ComponentConfigurationType,
  ComponentType,
  Document,
  DocumentVersion,
  Entitlement,
  SkillVisibility,
  SubComponentConfigurationType,
  SubComponentType,
} from "@models";
import { SvgIconComponent } from "@mui/icons-material";
import { GridColType } from "@mui/x-data-grid-premium";
import React from "react";
import { RoadmapVisibilityConditions } from "src/stores/models/roadmap-visibility-conditions";

// Minimum fields to create the entities (components, subcomponents, devices)
// Needed due to typescript weirdness which requires removing
// the fields we will update to work with ui
export type CreateComponentBase = Omit<CreateComponent, "subComponents">;
export type CreateDeviceBase = Omit<CreateDevice, "components">;

// Minimum fields + ui fields to create the entities (components, subcomponents, devices)
// Subcomponent with UI state
export type CreationModalSubComponentState = CreateSubComponent & {
  index: number;
};
// Component with UI state
export type CreationModalComponentState = CreateComponentBase & {
  collapsed?: boolean;
  index: number;
  subComponents?: CreationModalSubComponentState[];
};

export type CreateDeviceUiState = CreateDeviceBase & {
  components: CreationModalComponentState[];
};

export type CreateDemoDeviceRequest = {
  deviceConfig: {
    name: string;
    description: string;
    isDemoDevice?: boolean;
    isMDR?: boolean;
    isFDA?: boolean;
    isIVDR?: boolean;
    isUK?: boolean;
    isCanada?: boolean;
    components: Array<{
      name: string;
      type: ComponentType;
      description: string;
      configuration?: Array<{
        type: ComponentConfigurationType;
        value: string;
      }>;
      subComponents?: Array<{
        name?: string;
        type: SubComponentType;
        description: string;
        configuration?: Array<{
          type: SubComponentConfigurationType;
          value: string;
        }>;
      }>;
    }>;
  };
  documentsConfig: Array<{
    templateKey: string;
    title: string;
    version: {
      revisionSummary: string;
      answers: Array<{
        answer?: string;
        answerFileId?: string;
        element: string;
      }>;
      suggestions: Array<{
        suggestion: string;
        element: string;
      }>;
    };
  }>;
};

export type User = {
  id: string;
  onboarding: {
    callScheduled: boolean;
  };
  firstName: string;
  lastName: string;
  email: string;
  createdAt: Date;
  updatedAt: Date;
  organizationMemberships: Array<{
    organization: {
      id: string;
      name: string;
      companyDescription?: string | null;
      subscription: {
        products: string[];
        status: string;
      } | null;
    };
  }>;
  userUiState: {
    id: string;
    welcomeSeen: boolean;
    supportTooltipSeen: boolean;
    nextTodoCardTooltipSeen: boolean;
    approvalTooltipSeen: boolean;
    demoCompleteModalSeen: boolean;
    createdAt: Date;
    updatedAt: Date;
    userId: string;
  };
};

export enum CardContentType {
  GENERATE_TECHFILE = "GENERATE_TECHFILE",
}

export type QueryableFormElement = TextFieldElement | RadioButtonElement;

export interface FilePreUpload extends File {
  isUploaded: boolean;
}

export type DataKeyWithValue = {
  id: string;
  value: string | undefined | null;
};

export type DataKeyWithDefinedValue = {
  id: string;
  value: string;
};

export type SkillDraftOperation = "create" | "edit" | "duplicate";

type SkillDraftNamePolicy = "kebab" | "nonempty";

export type SkillDraftCardArgs = {
  operation?: SkillDraftOperation;
  sourceSkillId?: string;
  name: string;
  descriptionMd: string;
  contentMd: string;
  visibility: SkillVisibility;
  namePolicy?: SkillDraftNamePolicy;
};

export type RegulatoryType =
  | "RCD"
  | "SOP"
  | "TMP"
  | "LST"
  | "WIN"
  | "TCD"
  | "OTH"
  | "EXT";

export enum RegulatoryTypeEnum {
  RCD = "RCD",
  SOP = "SOP",
  TMP = "TMP",
  LST = "LST",
  WIN = "WIN",
  TCD = "TCD",
  OTH = "OTH",
  EXT = "EXT",
}

export type TemplateConfig = Record<string, Template>;

export interface Template extends DocumentTemplate {
  questions: Array<TemplateQuestion>;
}

export type TemplateQuestionWithoutActions = {
  prompt?: boolean;
  visibilityConditions?: RoadmapVisibilityConditions | null;
  required: boolean;
  id: string;
  videoUrl?: string;
  element:
    | TextFieldElement
    | SelectElement
    | TableElement
    | ParsedFileUploadElement
    | FileUploadElement
    | AnswerItemsElement
    | DateElement;
  context?: string[];
};

export type TemplateQuestion = TemplateQuestionWithoutActions & {
  prePromptTransformerConfig?: PrePromptTransformerConfig;
  questionTransformerConfig?: QuestionTransformerConfig;
  outputTransformer?: OutputTransformer;
  onUpload?: OnUpload;
};

export type OutputTransformer = {
  outputTransformer: ({
    answer,
    element,
  }: {
    answer: AnswerItem[];
    element: AnswerItemsElement;
  }) => string;
};

export type OnUpload = {
  onUpload: (file: File) => Promise<{ type: "table" | "json"; result: string }>;
};

export type QuestionTransformerConfig = {
  inputs: string[];
  transformer: (
    inputs: Record<string, string>,
    docReferences: Record<string, string>
  ) => string;
};

export type PrePromptTransformerConfig = {
  inputs: string[];
  transformer: (
    inputs: Record<string, string>
  ) => Array<{ id: string; value: string }>;
};

export type DataReferenceElement = {
  entity: "Device" | "Form";
  path: string;
  id: string;
};

declare global {
  interface Window {
    _env_: Record<string, any>;
  }
  namespace JSX {
    interface IntrinsicElements {
      "stripe-pricing-table": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

export type Suggestion = TSuggestion;

export type WorkflowState = {
  currentNode: string | undefined;
  isHiddenOutputNode: boolean;
  isLoading: boolean;
};

export type AnswerState = Partial<Record<string, string | null>>;

export type EnabledDocStatus = {
  enabled: boolean;
  incompleteTemplates?: string[];
};
export type DocProgress = Partial<EnabledDocStatus> & {
  title: string;
  docId: string;
  functionalGroupId: string | null;
  complete: boolean;
  noInstance?: boolean;
  noVersion?: boolean;
};
export type DocProgressMap = Partial<Record<Document["id"], DocProgress>>;

export enum DocumentStatus {
  AVAILABLE = "AVAILABLE",
  DRAFT = "DRAFT",
  UNAPPROVED = "UNAPPROVED",
  ARCHIVED = "ARCHIVED",
  LIVE = "LIVE",
}

export enum DocumentUIStatus {
  LOCKED = "LOCKED",
}

export type DocUIStatus =
  (typeof DocumentUIStatus)[keyof typeof DocumentUIStatus];

export type DocStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];

export type DocVersionWithStatusAndRevision = {
  status: DocStatus;
  revision: number;
} & DocumentVersion;

export type FunctionalGroup = {
  id: string;
  name: string;
  acronym: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  _count?: { members: number };
  entitlements?: Entitlement[];
};

export type FunctionalGroupMembership = {
  id: string;
  functionalGroupId: string;
  organizationMembershipId: string;
  functionalGroup: Pick<
    FunctionalGroup,
    "id" | "name" | "acronym" | "entitlements"
  >;
  createdAt: string;
};

export type PatchDocumentVersionData = Partial<
  Pick<
    DocumentVersion,
    | "readyForApproval"
    | "revisionSummary"
    | "contentTemplate"
    | "createdBy"
    | "changesFileId"
  >
>;

export enum ElementTypes {
  TextField = "textField",
  SelectElement = "select",
  table = "table",
  parsedFileUploadElement = "parsedFileUploadElement",
  fileUpload = "fileUpload",
  answerItemsElement = "answerItemsElement",
  dateElement = "dateInput",
  RadioButton = "radioButton",
  Text = "text",
}

export type ElementType = `${ElementTypes}`;

interface AbstractFormElement {
  // TODO remove string if we separated the form elements form the wizard elements
  id: string | string;
  type: ElementType;
  assistantQuestion?: string;
  answerType?: "short" | "elaborate";
  options?: Record<string, any>;
  visibilityCondition?: {
    id: string;
    value: string | boolean | number;
    section?: string;
  };
  disabledCondition?: {
    id: string;
    value: string | boolean | number;
    section?: string;
  };
  transformerConfig?: {
    inputs: string[];
    transformer: (
      inputs: Record<string, string>,
      docReferences: Record<string, string>
    ) => string;
  };
}

export interface TextFieldElement extends AbstractFormElement {
  type: "textField";
  options: {
    type?: "text" | "number";
    label: string;
    rows?: number;
    helperText?: string;
    default?: string;
  };
}

export interface ParsedFileUploadElement extends AbstractFormElement {
  type: "parsedFileUploadElement";
  options: {
    label: string;
    rows?: number;
    helperText?: string;
    default?: string;
    allowedFileTypes?: string;
    onUpload: (
      file: File
    ) => Promise<{ type: "table" | "json"; result: string }>;
  };
}

export interface AnswerItemsElement extends AbstractFormElement {
  type: "answerItemsElement";
  options: {
    typeId: string;
    outputTransformer: ({
      answer,
      element,
    }: {
      answer: AnswerItem[];
      element: AnswerItemsElement;
    }) => string;
    label: string;
    helperText?: string;
    default?: string;
    fields: {
      required?: boolean;
      disabled?: boolean;
      readOnly?: boolean;
      transformerConfig?: {
        inputs: string[];
        transformer: (inputs: Record<string, string>) => string | number;
      };
      fieldKey: string;
      label: string;
      element: ElementType;
      style?: React.CSSProperties;
      options?: Array<{
        value: string | boolean | number;
        label: string;
      }>;
      containerAlignment?: "row" | "column";
      rows?: number;
      default?: string;
      alwaysShown?: boolean;
      multiple?: boolean;
    }[];
  };
}

export interface FileUploadElement extends AbstractFormElement {
  type: "fileUpload";
  options: {
    label: string;
    helperText?: string;
    allowedFileTypes: string;
    default?: string;
  };
}

export interface DateElement extends AbstractFormElement {
  type: "dateInput";
  options: {
    label: string;
    helperText?: string;
    default?: string;
    hideLabel?: boolean;
  };
  style?: React.CSSProperties;
}

export interface SelectElement extends AbstractFormElement {
  type: "select";
  options: {
    label: string;
    options?: string[];
    // Index of the default value in the options array
    default?: string;
    multiple?: boolean;
    helperText?: string;
  };
}

export interface TableElement extends AbstractFormElement {
  type: "table";
  options: {
    columns: {
      name: string;

      options?: {
        flex?: number;
        type?: GridColType;
        maxWidth?: number;
        valueOptions?: string[];
        editable?: boolean;
        multiline?: boolean;
      };
    }[];
    idColumn?: string;
    default?: string;
    label: string;
    helperText?: string;
    addRowEnabled?: boolean;
  };
}

export interface RadioButtonElement extends AbstractFormElement {
  type: "radioButton";
  options: {
    label: string;
    containerAlignment?: "row" | "column";
    options: Array<{
      value: string | boolean | number;
      label: string;
    }>;
    default?: string;
  };
}

export type Fields = { [key: string]: string };

export type AnswerItem = {
  type: string;
  id: string;
  organizationId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  archived: boolean;
  sequentialTypeNumber: number;
  fields: Fields;
};

export type StepValue = {
  answer?: string;
  answerFileId?: string;
  answerItems?: AnswerItem[];
};

export type MultiStepFormInputElement =
  | TextFieldElement
  | SelectElement
  | TableElement
  | ParsedFileUploadElement
  | FileUploadElement
  | AnswerItemsElement
  | DateElement;

type BaseComponentConfigurationProps = {
  newDevice: CreateDeviceUiState;
  removeProductComponent: (component: CreationModalComponentState) => void;
  addProductComponent: (type: ComponentType) => void;
  toggleProductComponentCollapse: (
    component: CreationModalComponentState
  ) => void;
  updateProductComponent: (component: CreationModalComponentState) => void;
  updateComponentConfigurationValue: (
    component: CreationModalComponentState,
    configurationType: ComponentConfigurationType,
    value: string
  ) => void;
  getComponentConfigurationValue: (
    component: CreationModalComponentState,
    configurationType: ComponentConfigurationType
  ) => string | boolean | undefined;
};

export type SoftwareComponentConfigurationProps =
  BaseComponentConfigurationProps;

export type HardwareComponentConfigurationProps =
  BaseComponentConfigurationProps & {
    newDevice: CreateDeviceUiState;
    addSubComponent: (
      type: SubComponentType,
      component: CreationModalComponentState,
      name: string
    ) => void;
    removeAllSubComponentsByType: (
      component: CreationModalComponentState,
      subComponentType: SubComponentType
    ) => void;
    updateSubComponent: (
      component: CreationModalComponentState,
      subComponent: CreationModalSubComponentState
    ) => void;
    updateSubComponentConfigurationValue: (
      component: CreationModalComponentState,
      subComponent: CreationModalSubComponentState,
      type: SubComponentConfigurationType,
      value: string
    ) => void;
    getSubComponentConfigurationValue: (
      subComponent: CreationModalSubComponentState,
      configurationType: SubComponentConfigurationType
    ) => string | boolean | undefined;
  };

export type Leaf = {
  id: string;
  label: string;
  tooltipText: string;
  Icon: SvgIconComponent;
  color:
    | "disabled"
    | "inherit"
    | "action"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  link: string;
  highlightedText?: React.ReactNode;
  disabled?: boolean;
  doc?: Document;
};

export type WizardTopBarLeftButton = {
  startIcon?: React.ReactNode;
  onClick: () => void;
  text: string;
};

export type DeviceDocumentChangeAnswer = {
  dataKeyId: string;
  templateKey: string;
  documentId?: string;
  versionId?: string;
  previousAnswer?: StepValue;
  acceptedAnswer: StepValue;
  suggestion?: Suggestion;
};

export type SuggestionRequestBody = {
  characteristics: string[];
  device_name: string;
  device_description: string;
  additional_context: DataKeyWithValue[];
  options?: {
    batch_on?: string;
  };
  components: Component[];
  context_files: string[];
  step: {
    question: string;
    description: string;
    default_value: string | null;
    response_type: ElementType;
    output_config: Record<string, unknown>;
    id: string;
  };
};

/**
 * The state in the AssistantMultiStepForm is made up of an array of these.
 */
export type AssistantMultiStepFormAnswer = {
  dataKeyId: string;
  templateKey: string;
  previousAnswer: StepValue;
  acceptedAnswer?: StepValue;
  suggestion?: Suggestion;
};

export type ChangeSuggestionRequestBody = {
  change_information: string;
} & SuggestionRequestBody;

export type AssistantMultiStepFormAnswerUpdate = Pick<
  AssistantMultiStepFormAnswer,
  "acceptedAnswer" | "suggestion"
>;

export type AssistantMultiStepFormStep = {
  dataKeyId: string; // data key id of element to get answer from user
  templateKey: string; // template id of that element (potentially unnecessary as I can get on my own if need be)
};

export type StreamsManager = {
  has: (id: string) => boolean;
  set: (id: string, controller: AbortController) => void;
  delete: (id: string) => void;
};

export type NecessaryFunctionInfo = {
  functionName: string;
  dataKeyToFunctionMap: Record<string, OnUpload | OutputTransformer>;
};

export enum IntegrationType {
  SLACK = "slack",
  GITHUB = "github",
  LINEAR = "linear",
  JIRA = "jira",
}
export type IntegrationAuthModel =
  | "provider_oauth"
  | "mcp_oauth"
  | "mcp_none"
  | "manual_token"
  | "notification_only";

export type IntegrationAgentAccessMode =
  | "agent_tools"
  | "sync_only"
  | "notifications_only"
  | "none";
export type McpConnectionAuthMode = "manual_bearer" | "oauth" | "none";

export interface IntegrationConfig {
  id: IntegrationType;
  name: string;
  logo: string;
  description: string;
  status: "connected" | "disconnected" | "coming_soon";
  isComingSoon: boolean;
  isEnabled: boolean;
  isBeta?: boolean;
  authModel: IntegrationAuthModel;
  agentAccessMode: IntegrationAgentAccessMode;
  metadata?: any;
}

export type OAuthStatus = "loading" | "success" | "error";

export type FormData = {
  selectedUsers: Array<BaseUser | null>;
  selectedAuthor: BaseUser | null;
  revisionSummary: string;
  documentMetadata: {
    title?: Document["title"];
    regulatoryType?: Document["regulatoryType"];
    functionalGroupId?: Document["functionalGroupId"];
  };
};

export type UserToInvite = {
  email: string;
  firstName: string;
  lastName: string;
};

export type ButtonColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning";

export enum UserApprovalRoles {
  AUTHOR = "author",
  APPROVER = "approver",
  NOT_INVOLVED = "notInvolved",
}
